/**
 * ConsultarGTINsModal - Modal para consultar TODOS os GTINs cadastrados de um cliente
 *
 * Estratégia: client-side filtering. Faz UMA única requisição ao abrir o modal e
 * baixa TODOS os GTINs do cliente (até 200.000). Toda busca, ordenação, paginação
 * e filtros por coluna acontecem no browser via MantineReactTable em modo client-side.
 *
 * - Vantagem: filtragem instantânea (sem roundtrip por keystroke)
 * - Custo: ~1-2s de fetch inicial + 10-15MB JSON para o pior caso (Medtronic ~67k GTINs)
 * - Performance ok graças à row virtualization do MRT
 *
 * Cada linha representa UM GTIN. Produtos com múltiplos GTINs aparecem em múltiplas
 * linhas. A coluna "Principal" marca qual é o gtin_principal do produto.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import { Badge, Loader } from '@mantine/core';
import { produtoClienteService } from '../../services/produtoClienteService';
import type { GTINConsultaItem } from '../../types/produtoCliente';
import { MRT_LOCALIZATION_PT_BR } from '../hospital/tableLocalization';

interface ConsultarGTINsModalProps {
  show: boolean;
  clienteId: number | null;
  clienteNome?: string;
  onClose: () => void;
}

// Limite de fetch — alinhado com o cap do backend (max 200.000)
const FETCH_PAGE_SIZE = 200000;

export const ConsultarGTINsModal: React.FC<ConsultarGTINsModalProps> = ({
  show,
  clienteId,
  clienteNome,
  onClose,
}) => {
  const [data, setData] = useState<GTINConsultaItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Single fetch on open (or when cliente changes)
  useEffect(() => {
    if (!show || !clienteId) {
      setData([]);
      setTotal(0);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData([]);
    setTotal(0);

    produtoClienteService
      .consultarGTINs(clienteId, { page: 1, pageSize: FETCH_PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setData(res.items);
        setTotal(res.total);
      })
      .catch((err) => {
        if (cancelled) return;
        const detail =
          err?.response?.data?.detail || err?.message || 'Erro ao consultar GTINs';
        setError(detail);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, clienteId]);

  const columns = useMemo<MRT_ColumnDef<GTINConsultaItem>[]>(
    () => [
      {
        accessorKey: 'gtin',
        header: 'GTIN',
        size: 170,
        Cell: ({ cell }) => (
          <span className="font-mono text-xs font-medium text-gray-900 dark:text-gray-200">
            {cell.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: 'is_principal',
        header: 'Principal',
        size: 90,
        // Use accessorFn to make filtering work on the boolean as a string
        accessorFn: (row) => (row.is_principal ? 'Sim' : 'Não'),
        Cell: ({ row }) =>
          row.original.is_principal ? (
            <Badge color="purple" variant="filled" size="xs">
              Sim
            </Badge>
          ) : (
            <Badge color="gray" variant="light" size="xs">
              Não
            </Badge>
          ),
        filterVariant: 'select',
        mantineFilterSelectProps: { data: ['Sim', 'Não'] },
      },
      {
        accessorKey: 'gtin_ativo',
        header: 'Ativo',
        size: 80,
        accessorFn: (row) => (row.gtin_ativo ? 'Sim' : 'Não'),
        Cell: ({ row }) => (
          <Badge
            color={row.original.gtin_ativo ? 'green' : 'red'}
            variant="light"
            size="xs"
          >
            {row.original.gtin_ativo ? 'Sim' : 'Não'}
          </Badge>
        ),
        filterVariant: 'select',
        mantineFilterSelectProps: { data: ['Sim', 'Não'] },
      },
      {
        accessorKey: 'codigo',
        header: 'Código',
        size: 130,
        Cell: ({ cell }) => (
          <span className="font-mono text-xs text-gray-900 dark:text-gray-300">
            {cell.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: 'descricao_completa',
        header: 'Descrição',
        size: 280,
        Cell: ({ cell }) => (
          <span
            className="text-sm text-gray-900 dark:text-gray-300 line-clamp-2"
            title={cell.getValue<string>()}
          >
            {cell.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: 'grupo',
        header: 'Grupo',
        size: 130,
        Cell: ({ cell }) => {
          const v = cell.getValue<string>();
          return v ? (
            <Badge size="xs" variant="light" color="blue">
              {v}
            </Badge>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          );
        },
      },
      {
        accessorKey: 'fabricante',
        header: 'Fabricante',
        size: 150,
        Cell: ({ cell }) => (
          <span
            className="text-xs text-gray-700 dark:text-gray-400 truncate"
            title={cell.getValue<string>() || ''}
          >
            {cell.getValue<string>() || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'unidade_medida',
        header: 'UM',
        size: 60,
        Cell: ({ cell }) => (
          <span className="text-xs text-gray-700 dark:text-gray-400">
            {cell.getValue<string>() || '-'}
          </span>
        ),
      },
    ],
    []
  );

  // ALL client-side: no manualPagination, no manualFiltering, no manualSorting
  const table = useMantineReactTable({
    columns,
    data,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: true,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enableRowActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowVirtualization: true, // critical for 67k rows
    initialState: {
      density: 'xs',
      pagination: { pageIndex: 0, pageSize: 100 },
      showGlobalFilter: true,
    },
    state: {
      isLoading: loading,
    },
    mantineSearchTextInputProps: {
      placeholder: 'Busque por GTIN, código, descrição, grupo ou fabricante...',
      style: { minWidth: '420px' },
    },
    mantineTableContainerProps: { style: { maxHeight: '60vh' } },
    mantineTableProps: {
      highlightOnHover: true,
      striped: true,
      withTableBorder: true,
      withColumnBorders: true,
      sx: {
        th: { padding: '8px 12px !important' },
        td: { padding: '4px 12px !important' },
      },
    },
    mantinePaperProps: { shadow: 'none', withBorder: true, radius: 'md' },
    localization: MRT_LOCALIZATION_PT_BR,
  });

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Consultar GTINs Cadastrados
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {clienteNome ? `Cliente: ${clienteNome}` : 'Selecione um cliente'}
              {!loading && total > 0 && (
                <>
                  {' '}
                  — <strong>{total.toLocaleString('pt-BR')}</strong> GTINs carregados
                  (busca, filtros e ordenação são instantâneos)
                </>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Fechar"
          >
            <X size={24} />
          </button>
        </div>

        {/* Loading / Error / Table */}
        <div className="flex-1 overflow-auto p-4">
          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader size="lg" color="purple" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Carregando todos os GTINs do cliente...
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                Pode levar alguns segundos para clientes com muitos cadastros
              </p>
            </div>
          ) : (
            <MantineReactTable table={table} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultarGTINsModal;
